/**
 * useShanksChatConnect — direct browser → sabaody → shanks chat hook.
 *
 * The only chat-streaming hook on the /assistant surface. Built to
 * mirror the (still-used-by-AISidebarShell) useShanksChat return
 * shape so consumers can read messages[i].toolCalls etc. the same
 * way. Implementation notes:
 *
 *   - Streams via agentClient.chat() (Connect-Web server-streaming)
 *     rather than fetch('/api/v2/chat/stream') + manual SSE parsing.
 *   - Drops user_id / tenant_id from the request body — sabaody
 *     injects them from the validated JWT cookie on every proxied
 *     RPC, so the values in the body would just be ignored.
 *   - Maps proto ChatResponse.eventType ("message" | "tool_call" |
 *     "tool_result" | "artifact" | "done" | "error") to the existing
 *     in-memory state shape.
 *
 * The proto-event-name → internal-state mapping intentionally
 * matches what the express SSE bridge produced via its three-step
 * tool_call_start / tool_call_delta / tool_call_end fan-out
 * (services/express-backend/src/app.ts:419-481) — same end state,
 * just expressed once on the client instead of three times across
 * the wire.
 */ import { ConnectError } from '@connectrpc/connect';
import { AgentTypes } from '@mono/proto-clients';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toChatContextInit } from '../lib/chat/envelope';
import { agentClient } from '../lib/connect/clients';
// =============================================================================
// Hook
// =============================================================================
export function useShanksChatConnect({ chatId, initialMessages = [], initialConversationId, projectId, context, onFinish, onAwaitingApproval, onError, onArtifact, onTitle, onConversationId, restoredSubmission }) {
    const [messages, setMessages] = useState(initialMessages);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [incomplete, setIncomplete] = useState(false);
    const [conversationId, setConversationId] = useState(initialConversationId || chatId.replace(/^shanks-/, ''));
    const [artifacts, setArtifacts] = useState([]);
    // Track tool calls during a streaming response so multiple proto
    // events for the same tool_call_id (call followed by result) merge
    // into a single ShanksToolCall in the rendered message.
    const currentToolCallsRef = useRef(new Map());
    // Remembers the last submitted message so retry() can replay it after a
    // failure without the caller re-supplying content/fileIds.
    const lastSubmissionRef = useRef(restoredSubmission ?? null);
    const remoteRunning = restoredSubmission?.state === 'running';
    const savedCompleted = restoredSubmission?.id === lastSubmissionRef.current?.id && [
        'done',
        'awaiting_approval'
    ].includes(restoredSubmission?.state ?? '');
    useEffect(()=>{
        if (savedCompleted && !isLoading) {
            setError(null);
            setIncomplete(false);
        }
    }, [
        savedCompleted,
        isLoading
    ]);
    useEffect(()=>{
        if (restoredSubmission && !isLoading && (!lastSubmissionRef.current || lastSubmissionRef.current.id === restoredSubmission.id)) lastSubmissionRef.current = restoredSubmission;
    }, [
        restoredSubmission,
        isLoading
    ]);
    // AbortController scoped to the in-flight Chat() server-stream.
    // We pass its signal into agentClient.chat({signal}) so an unmount
    // mid-response (user navigates away, swaps chat, etc.) aborts the
    // stream cleanly: the for-await loop throws AbortError, the catch
    // block sees it and skips state updates, and the network/decoder
    // work stops instead of running to completion in the background.
    // Without this, setState calls inside the loop would also fire on
    // an unmounted component (React 18 dev-mode warning).
    //
    // StrictMode note: React 18 dev runs every effect → cleanup → effect
    // on first mount to surface unsafe patterns. If we aborted directly
    // in the cleanup, every fresh ChatView would see its first Chat RPC
    // killed before it left the browser. We defer the abort by one task
    // and check `isMountedRef` — set false here, set back true on the
    // remount — so the StrictMode cycle no-ops while a real unmount
    // still aborts on the next tick.
    const streamAbortRef = useRef(null);
    const isMountedRef = useRef(true);
    useEffect(()=>{
        isMountedRef.current = true;
        return ()=>{
            isMountedRef.current = false;
            const controller = streamAbortRef.current;
            setTimeout(()=>{
                if (!isMountedRef.current) {
                    controller?.abort();
                }
            }, 0);
        };
    }, []);
    const sessionIdentity = useRef(chatId);
    useEffect(()=>{
        if (sessionIdentity.current === chatId) return;
        sessionIdentity.current = chatId;
        streamAbortRef.current?.abort();
        streamAbortRef.current = null;
        setMessages(initialMessages);
        setConversationId(initialConversationId || chatId.replace(/^shanks-/, ''));
        setError(null);
        setIsLoading(false);
        setIncomplete(false);
        currentToolCallsRef.current = new Map();
        lastSubmissionRef.current = restoredSubmission ?? null;
    }, [
        chatId,
        initialConversationId,
        initialMessages,
        restoredSubmission
    ]);
    const applyToolCallsToMessage = useCallback((assistantMessageId)=>{
        setMessages((prev)=>prev.map((m)=>m.id === assistantMessageId ? {
                    ...m,
                    toolCalls: Array.from(currentToolCallsRef.current.values())
                } : m));
    }, []);
    // Shared streaming core. Owns the abort controller, loading/error state,
    // the tool-call accumulator, and the proto-event → state mapping. Both
    // sendMessage (fresh turn) and retry (replay after failure) drive it; they
    // differ only in which messages they append before streaming.
    const runStream = useCallback(async (assistantMessageId, content, fileIds, submissionId)=>{
        setIsLoading(true);
        setError(null);
        setIncomplete(false);
        currentToolCallsRef.current = new Map();
        // Abort any prior in-flight stream and arm a fresh controller so
        // the unmount cleanup at the top of the hook can cancel just
        // this stream cleanly. Sending a second message while the first
        // is still streaming is gated by `isLoading`, but defensive abort
        // is cheap and protects against future code paths that bypass it.
        streamAbortRef.current?.abort();
        const abort = new AbortController();
        streamAbortRef.current = abort;
        let endedAwaitingApproval = false;
        let liveAssistantId = assistantMessageId;
        try {
            // No pre-flight token refresh needed: connectTransport's
            // authInterceptor (src/lib/connect/transport.ts) already retries
            // on Code.Unauthenticated by calling refreshAccessToken under
            // the hood. The express SSE path needed manual refresh because
            // raw fetch() can't retry mid-stream; Connect-Web doesn't have
            // that limitation for unary, and for streaming the interceptor
            // wraps the initial open.
            const request = new AgentTypes.ChatRequest({
                message: content.trim(),
                submissionId: submissionId ?? '',
                conversationId: conversationId ?? '',
                fileIds: fileIds ?? [],
                projectId: context?.projectId || projectId || '',
                // userId / tenantId left empty — sabaody injects them from
                // the validated cookie on every proxied call.
                context: context ? new AgentTypes.ChatContext(toChatContextInit(context)) : undefined
            });
            for await (const event of agentClient.chat(request, {
                signal: abort.signal
            })){
                switch(event.eventType){
                    case 'conversation_id':
                        if (event.assistantMessageId) {
                            const previousId = liveAssistantId;
                            liveAssistantId = event.assistantMessageId;
                            setMessages((prev)=>prev.map((message)=>{
                                    if (message.id === previousId) return {
                                        ...message,
                                        id: event.assistantMessageId
                                    };
                                    if (message.id === submissionId && event.userMessageId) return {
                                        ...message,
                                        id: event.userMessageId
                                    };
                                    return message;
                                }));
                        }
                        if (event.conversationId) {
                            setConversationId(event.conversationId);
                            onConversationId?.(event.conversationId);
                        }
                        break;
                    case 'message':
                        setMessages((prev)=>prev.map((m)=>{
                                if (m.id !== liveAssistantId) return m;
                                const text = event.content;
                                // Merge with the trailing text part if any, otherwise append
                                // a new one — preserves interleaving with reasoning/tool calls.
                                const parts = [
                                    ...m.parts ?? []
                                ];
                                const last = parts[parts.length - 1];
                                if (last?.type === 'text') {
                                    parts[parts.length - 1] = {
                                        ...last,
                                        content: last.content + text
                                    };
                                } else {
                                    parts.push({
                                        type: 'text',
                                        content: text
                                    });
                                }
                                return {
                                    ...m,
                                    content: m.content + text,
                                    parts
                                };
                            }));
                        break;
                    case 'reasoning':
                        setMessages((prev)=>prev.map((m)=>{
                                if (m.id !== liveAssistantId) return m;
                                const text = event.content;
                                const parts = [
                                    ...m.parts ?? []
                                ];
                                const last = parts[parts.length - 1];
                                if (last?.type === 'reasoning') {
                                    parts[parts.length - 1] = {
                                        ...last,
                                        content: last.content + text
                                    };
                                } else {
                                    parts.push({
                                        type: 'reasoning',
                                        content: text
                                    });
                                }
                                return {
                                    ...m,
                                    reasoning: (m.reasoning ?? '') + text,
                                    parts
                                };
                            }));
                        break;
                    case 'tool_call':
                        if (event.toolCall) {
                            const toolCall = event.toolCall;
                            const argsJson = toolCall.arguments ? JSON.stringify(toolCall.arguments.toJson()) : undefined;
                            const existing = currentToolCallsRef.current.get(toolCall.id);
                            currentToolCallsRef.current.set(toolCall.id, {
                                ...existing,
                                id: toolCall.id,
                                name: toolCall.name,
                                arguments: argsJson ?? existing?.arguments,
                                purpose: toolCall.purpose || existing?.purpose,
                                executionState: toolCall.executionState || existing?.executionState
                            });
                            // Append a tool_call part so order is preserved with reasoning/text.
                            // The fully-realized tool result is still rendered from msg.toolCalls;
                            // this part only marks the position in the stream.
                            setMessages((prev)=>prev.map((m)=>{
                                    if (m.id !== liveAssistantId) return m;
                                    const parts = [
                                        ...m.parts ?? []
                                    ];
                                    if (!parts.some((part)=>part.type === 'tool_call' && part.toolCallId === toolCall.id)) parts.push({
                                        type: 'tool_call',
                                        content: '',
                                        toolCallId: toolCall.id,
                                        toolName: toolCall.name
                                    });
                                    return {
                                        ...m,
                                        parts
                                    };
                                }));
                            applyToolCallsToMessage(liveAssistantId);
                        }
                        break;
                    case 'tool_result':
                        if (event.toolResult) {
                            const existing = currentToolCallsRef.current.get(event.toolResult.toolCallId);
                            // Preserve name/arguments from the prior tool_call event
                            // when present; tool_result alone (without a preceding
                            // tool_call) shouldn't lose identity. On error, we set
                            // both `result` and `error` from the same content string
                            // — this matches the legacy SSE bridge shape (express
                            // app.ts:451-455 sends `result: content, error: isError ?
                            // content : undefined`), so downstream renderers don't
                            // need a Connect-vs-bridge branch. ChatView's
                            // convertShanksToChatMessages then collapses to
                            // `output: { error: tc.error }` last, so the rendered
                            // shape is identical regardless of source.
                            currentToolCallsRef.current.set(event.toolResult.toolCallId, {
                                id: event.toolResult.toolCallId,
                                name: existing?.name ?? '',
                                arguments: existing?.arguments,
                                purpose: existing?.purpose,
                                executionState: existing?.executionState,
                                duration: event.toolResult.durationMs || undefined,
                                result: event.toolResult.content,
                                error: event.toolResult.isError ? event.toolResult.content : undefined
                            });
                            applyToolCallsToMessage(liveAssistantId);
                        }
                        break;
                    case 'artifact':
                        if (event.artifact) {
                            const artifact = {
                                id: event.artifact.id,
                                title: event.artifact.title,
                                kind: event.artifact.kind,
                                content: event.artifact.content,
                                sourceAttachmentId: event.artifact.sourceAttachmentId || undefined,
                                sourceUrl: event.artifact.sourceUrl || undefined,
                                contentType: event.artifact.contentType || undefined
                            };
                            setArtifacts((prev)=>[
                                    ...prev,
                                    artifact
                                ]);
                            onArtifact?.(artifact);
                        }
                        break;
                    case 'title':
                        // Server-generated conversation title (first turn). The caller
                        // writes it into the conversation/history caches so the header
                        // and switcher update live.
                        if (event.title) {
                            onTitle?.(event.title);
                        }
                        break;
                    case 'awaiting_approval':
                        applyToolCallsToMessage(liveAssistantId);
                        endedAwaitingApproval = true;
                        break;
                    case 'done':
                        applyToolCallsToMessage(liveAssistantId);
                        break;
                    case 'incomplete':
                        applyToolCallsToMessage(liveAssistantId);
                        setIncomplete(true);
                        setError(event.error || 'Your progress is saved. Continue to finish this request.');
                        break;
                    case 'error':
                        if (event.error) {
                            setError(event.error);
                        }
                        break;
                    default:
                        // Unknown event type — log and continue. Better than throwing
                        // mid-stream and dropping the rest of the response.
                        if (event.eventType) {
                            console.warn('[useShanksChatConnect] unknown event type:', event.eventType);
                        }
                        break;
                }
            }
            if (endedAwaitingApproval) {
                onAwaitingApproval?.();
            } else {
                onFinish?.();
            }
        } catch (err) {
            // Caller-initiated abort (unmount cleanup or send-after-send):
            // bail without setting error state or invoking onError. The
            // component is either gone (state setters are no-ops) or
            // about to overwrite this stream's results, so reporting an
            // error would only confuse the user. Connect-Web surfaces
            // aborts as a ConnectError with Code.Canceled.
            if (abort.signal.aborted) {
                // Stopped by the user (or unmount). Keep whatever streamed so far,
                // but drop the assistant placeholder if it's still empty so a stop
                // before the first token doesn't leave a blank assistant bubble.
                setMessages((prev)=>prev.filter((m)=>{
                        if (m.id !== liveAssistantId) return true;
                        return !!m.content || !!m.reasoning || (m.parts?.length ?? 0) > 0 || (m.toolCalls?.length ?? 0) > 0;
                    }));
                return;
            }
            // Connect-Web surfaces all RPC errors as ConnectError; keep its
            // code in the message for diagnostics, but the caller-facing
            // shape stays a plain string (matches useShanksChat).
            const errorMessage = err instanceof ConnectError ? `${err.code}: ${err.rawMessage}` : err instanceof Error ? err.message : 'An error occurred';
            setError(errorMessage);
            // Remove the empty assistant placeholder on error — same
            // behaviour as useShanksChat so a failed send doesn't leave
            // a blank assistant bubble in the thread.
            setMessages((prev)=>prev.filter((m)=>m.id !== liveAssistantId || !!m.content || !!m.reasoning || (m.parts?.length ?? 0) > 0 || (m.toolCalls?.length ?? 0) > 0));
            onError?.(err instanceof Error ? err : new Error(errorMessage));
        } finally{
            // Only flip isLoading off if this stream is the one that's
            // still armed; a later sendMessage call has already replaced
            // the controller and is managing its own loading state.
            if (streamAbortRef.current === abort) {
                streamAbortRef.current = null;
                setIsLoading(false);
            }
        }
    }, [
        conversationId,
        projectId,
        context,
        applyToolCallsToMessage,
        onArtifact,
        onAwaitingApproval,
        onFinish,
        onError,
        onTitle,
        onConversationId
    ]);
    const sendMessage = useCallback(async (content, options)=>{
        if (!content.trim() || isLoading || remoteRunning) return;
        const userMessage = {
            id: crypto.randomUUID(),
            role: 'user',
            content: content.trim()
        };
        const assistantMessageId = crypto.randomUUID();
        const assistantMessage = {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            toolCalls: []
        };
        setMessages((prev)=>[
                ...prev,
                userMessage,
                assistantMessage
            ]);
        lastSubmissionRef.current = {
            id: userMessage.id,
            content: content.trim(),
            fileIds: options?.fileIds
        };
        await runStream(assistantMessageId, content.trim(), options?.fileIds, userMessage.id);
    }, [
        isLoading,
        remoteRunning,
        runStream
    ]);
    // Stop the in-flight stream. The running runStream closure's catch sees
    // abort.signal.aborted and bails without setError/onError; its finally
    // flips isLoading off. Any output already streamed is intentionally kept
    // (ChatGPT-style stop). ChatView resets its own isSending separately,
    // since aborting skips the onFinish/onError callbacks.
    const stop = useCallback(async ()=>{
        const submission = lastSubmissionRef.current;
        if (submission && conversationId) {
            try {
                await agentClient.stopChatSubmission({
                    conversationId,
                    submissionId: submission.id
                });
            } catch (error) {
                const failure = error instanceof Error ? error : new Error('The request could not be stopped. Retry.');
                setError(failure.message);
                onError?.(failure);
                return false;
            }
        }
        streamAbortRef.current?.abort();
        return true;
    }, [
        conversationId,
        onError
    ]);
    // Re-run the last submitted message after a failure. On error only the
    // assistant placeholder is dropped — the user message survives — so we
    // append a fresh placeholder and replay, avoiding a duplicate user bubble.
    const retry = useCallback(async ()=>{
        const last = lastSubmissionRef.current;
        if (!last || isLoading || remoteRunning) return;
        if (incomplete || restoredSubmission?.state === 'incomplete' && last.id === restoredSubmission.id) {
            await sendMessage('Continue the unfinished request from the saved progress. Check what is already done and finish the remaining work.', {
                fileIds: last.fileIds
            });
            return;
        }
        const assistantMessageId = crypto.randomUUID();
        const assistantMessage = {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            toolCalls: []
        };
        setMessages((prev)=>[
                ...prev,
                assistantMessage
            ]);
        await runStream(assistantMessageId, last.content, last.fileIds, last.id);
    }, [
        isLoading,
        remoteRunning,
        runStream,
        incomplete,
        restoredSubmission?.state,
        restoredSubmission?.id,
        sendMessage
    ]);
    const append = useCallback(async (message, options)=>{
        if (message.role === 'user') {
            await sendMessage(message.content, options);
        }
    }, [
        sendMessage
    ]);
    const reset = useCallback(()=>{
        // Abort any in-flight stream first — without this, late stream events
        // would race the cleared state and re-populate messages after reset.
        streamAbortRef.current?.abort();
        streamAbortRef.current = null;
        setMessages([]);
        setConversationId(null);
        setError(null);
        setIncomplete(false);
        setIsLoading(false);
        currentToolCallsRef.current = new Map();
        lastSubmissionRef.current = null;
    }, []);
    return {
        messages,
        sendMessage,
        append,
        stop,
        retry,
        isLoading,
        error: isLoading || remoteRunning || savedCompleted ? null : error,
        incomplete,
        conversationId,
        artifacts,
        reset
    };
}
